import { withHoneypot } from "@/lib/honeypot"

/**
 * The browser side of POST /api/newsletter, shared by both signup forms (the
 * intro popup and the footer/inline form), so the request and its answer are
 * read one way.
 *
 * Bounded on purpose. The route stores the subscriber in well under a second
 * (in the incident that set this limit the row was written 0.2 s after the POST
 * reached Vercel), but on a phone whose connection has stalled the browser
 * holds a fetch with no limit of its own: that iPhone visitor's POST sat in the
 * phone's network stack for 78 seconds while the button said "Sending..." and
 * offered no way out. Past the
 * limit the request is abandoned and the form gets the same retryable network
 * error it shows for a dropped connection. A retry is safe: the route treats an
 * address it already holds as an existing subscriber, so a stalled first
 * request that lands late never sends a second welcome email.
 */
export const NEWSLETTER_SIGNUP_TIMEOUT_MS = 15_000

const NETWORK_ERROR = "Network error. Try again."
const GENERIC_ERROR = "Could not sign you up. Try again."

export type NewsletterSignupResult =
  /** The address is on the list and our email can reach it. */
  | { kind: "subscribed" }
  /** Saved, but our email cannot reach the address (a bounce or complaint
   * suppression, or a team address): the form must not say "check your email". */
  | { kind: "unreachable" }
  /** Nothing to celebrate: the message is what the form shows, and the form
   * can be sent again. */
  | { kind: "error"; message: string }

/** POST the signup. `form` is the submitting form element, read for the
 * honeypot field; pass the event's `currentTarget` before any await. */
export async function requestNewsletterSignup(
  email: string,
  source: string,
  form: EventTarget | null,
): Promise<NewsletterSignupResult> {
  const body = JSON.stringify(withHoneypot({ email, source }, form))
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), NEWSLETTER_SIGNUP_TIMEOUT_MS)
  try {
    const res = await fetch("/api/newsletter", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      signal: controller.signal,
    })
    const data = (await res.json().catch(() => ({}))) as { error?: string; subscribed?: boolean }
    if (controller.signal.aborted) return { kind: "error", message: NETWORK_ERROR }
    if (!res.ok) return { kind: "error", message: data.error || GENERIC_ERROR }
    if (data.subscribed === false) return { kind: "unreachable" }
    return { kind: "subscribed" }
  } catch {
    return { kind: "error", message: NETWORK_ERROR }
  } finally {
    clearTimeout(timer)
  }
}
